import { useState, type ReactNode } from "react";
import { Lock } from "lucide-react";
import LockedFeatureSheet, { type FeatureKey } from "./LockedFeatureSheet";

interface LockedButtonProps {
  locked: boolean;
  feature: FeatureKey;
  children: ReactNode;
  className?: string;
}

/**
 * Wraps any element. When `locked`, dims the child, blocks pointer events on
 * the child, overlays a small lock icon, and intercepts clicks to open
 * LockedFeatureSheet for the relevant feature.
 */
const LockedButton = ({
  locked,
  feature,
  children,
  className,
}: LockedButtonProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  if (!locked) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        className={`relative inline-block ${className ?? ""}`}
        onClick={(e) => {
          e.stopPropagation();
          setSheetOpen(true);
        }}
        role="button"
        tabIndex={0}
      >
        <div style={{ opacity: 0.4, pointerEvents: "none" }}>{children}</div>
        <div
          aria-hidden
          className="absolute top-1 right-1 flex items-center justify-center rounded-full bg-background/90 shadow-sm"
          style={{ width: 14, height: 14 }}
        >
          <Lock style={{ width: 10, height: 10 }} className="text-foreground/70" />
        </div>
      </div>

      <LockedFeatureSheet
        feature={feature}
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
};

export default LockedButton;
